/**
 * AssignmentReviewStep - Step 2 of the Client Transfer Wizard
 * Review and decide on BCBA + RBT assignments after transfer.
 */

import styled from 'styled-components';
import { UserCheck, Users, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';

const StepRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.status.warning.text};
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const WarningBannerText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.warning.text};
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
`;

const SectionCard = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const SectionTitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SectionBody = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const CurrentInfo = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const RadioOption = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.primary.base : theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.backgrounds.light : theme.colors.backgrounds.white};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.sky};
  }

  input[type='radio'] {
    accent-color: ${({ theme }) => theme.colors.primary.base};
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const RadioLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RbtTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const RbtTableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
`;

const RbtTableHeaderCell = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const RbtRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${({ theme }) => theme.spacing[4]};
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const RbtName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ToggleGroup = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary.base : theme.colors.backgrounds.white};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.white : theme.colors.text.secondary};
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.borders.light};
  }

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primary.base : theme.colors.backgrounds.light};
  }
`;

const EmptyRbt = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: center;
`;

const StepActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

export interface BcbaDecision {
  action: 'keep' | 'reassign';
  newBcbaId?: string;
}

export interface RbtDecision {
  rbtMemberId: string;
  action: 'keep' | 'remove';
}

interface RbtInfo {
  rbtMemberId: string;
  name: string;
}

interface AssignmentReviewStepProps {
  newCenterName: string;
  currentBcbaName?: string;
  currentBcbaCenterName?: string;
  rbts: RbtInfo[];
  bcbaDecision: BcbaDecision;
  rbtDecisions: RbtDecision[];
  onBcbaDecisionChange: (decision: BcbaDecision) => void;
  onRbtDecisionChange: (memberId: string, action: 'keep' | 'remove') => void;
  onComplete: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export const AssignmentReviewStep = ({
  newCenterName,
  currentBcbaName,
  currentBcbaCenterName,
  rbts,
  bcbaDecision,
  rbtDecisions,
  onBcbaDecisionChange,
  onRbtDecisionChange,
  onComplete,
  onBack,
  isSubmitting,
}: AssignmentReviewStepProps) => {
  const getRbtDecision = (memberId: string): 'keep' | 'remove' => {
    return rbtDecisions.find((d) => d.rbtMemberId === memberId)?.action ?? 'keep';
  };

  return (
    <StepRoot>
      <WarningBanner>
        <AlertCircle />
        <WarningBannerText>
          This client is being transferred to <strong>{newCenterName}</strong>. Review the existing
          provider assignments and decide whether to keep or remove them.
        </WarningBannerText>
      </WarningBanner>

      {/* BCBA Section */}
      <SectionCard>
        <SectionHeader>
          <UserCheck />
          <SectionTitle>BCBA Assignment</SectionTitle>
        </SectionHeader>
        <SectionBody>
          {currentBcbaName ? (
            <CurrentInfo>
              Current BCBA: <strong>{currentBcbaName}</strong>
              {currentBcbaCenterName && (
                <> at <strong>{currentBcbaCenterName}</strong></>
              )}
            </CurrentInfo>
          ) : (
            <CurrentInfo>No BCBA currently assigned.</CurrentInfo>
          )}

          <RadioGroup role="radiogroup" aria-label="BCBA assignment decision">
            <RadioOption $selected={bcbaDecision.action === 'keep'}>
              <input
                type="radio"
                name="bcbaDecision"
                value="keep"
                checked={bcbaDecision.action === 'keep'}
                onChange={() => onBcbaDecisionChange({ action: 'keep' })}
              />
              <RadioLabel>Keep current BCBA assignment</RadioLabel>
            </RadioOption>
            <RadioOption $selected={bcbaDecision.action === 'reassign'}>
              <input
                type="radio"
                name="bcbaDecision"
                value="reassign"
                checked={bcbaDecision.action === 'reassign'}
                onChange={() => onBcbaDecisionChange({ action: 'reassign' })}
              />
              <RadioLabel>
                Reassign BCBA at new center (can be completed after transfer)
              </RadioLabel>
            </RadioOption>
          </RadioGroup>
        </SectionBody>
      </SectionCard>

      {/* RBT Section */}
      <SectionCard>
        <SectionHeader>
          <Users />
          <SectionTitle>RBT Assignments</SectionTitle>
        </SectionHeader>
        <SectionBody>
          {rbts.length === 0 ? (
            <EmptyRbt>No RBTs currently assigned to this client.</EmptyRbt>
          ) : (
            <RbtTable>
              <RbtTableHeader>
                <RbtTableHeaderCell>RBT Name</RbtTableHeaderCell>
                <RbtTableHeaderCell>Action</RbtTableHeaderCell>
              </RbtTableHeader>
              {rbts.map((rbt) => {
                const decision = getRbtDecision(rbt.rbtMemberId);
                return (
                  <RbtRow key={rbt.rbtMemberId}>
                    <RbtName>{rbt.name}</RbtName>
                    <ToggleGroup role="group" aria-label={`Action for ${rbt.name}`}>
                      <ToggleButton
                        type="button"
                        $active={decision === 'keep'}
                        onClick={() => onRbtDecisionChange(rbt.rbtMemberId, 'keep')}
                        aria-pressed={decision === 'keep'}
                      >
                        Keep
                      </ToggleButton>
                      <ToggleButton
                        type="button"
                        $active={decision === 'remove'}
                        onClick={() => onRbtDecisionChange(rbt.rbtMemberId, 'remove')}
                        aria-pressed={decision === 'remove'}
                      >
                        Remove
                      </ToggleButton>
                    </ToggleGroup>
                  </RbtRow>
                );
              })}
            </RbtTable>
          )}
        </SectionBody>
      </SectionCard>

      <StepActions>
        <Button variant="ghost" type="button" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          variant="gradient"
          type="button"
          isLoading={isSubmitting}
          rightIcon={<ChevronRight />}
          onClick={onComplete}
        >
          Complete Transfer
        </Button>
      </StepActions>
    </StepRoot>
  );
};

export default AssignmentReviewStep;
